import { useCallback, useEffect, useMemo, useState } from 'react';

import { Platform } from 'react-native';

import { useTranslation } from 'react-i18next';
import ENV from 'react-native-config';
import {
    getBrand,
    getDevice,
    getModel,
    getSystemVersion,
    getVersion,
} from 'react-native-device-info';
import { useSelector } from 'react-redux';

import { format } from 'date-fns';

import { getEpgLastUpdated } from 'store/epg/selectors';
import { selectAccountId, selectClientId } from 'store/user/selectors';

import { buildNumber, version } from '../../../../package.json';
import {
    SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE,
    SYSTEM_INFORMATION_ACCOUNT_ID,
    SYSTEM_INFORMATION_ENVIRONMENT,
    SYSTEM_INFORMATION_EPG_DATA,
    SYSTEM_INFORMATION_HARDWARE_BRAND_NAME,
    SYSTEM_INFORMATION_HARDWARE_VERSION,
    SYSTEM_INFORMATION_MODEL_NAME,
    SYSTEM_INFORMATION_SOFTWARE_VERSION,
    SYSTEM_INFORMATION_UI_VERSION,
    SYSTEM_INFORMATION_UUID,
} from '../languagesConst';
import { SimpleSettingsItem } from '../types';
import detectBrowserInfo from './utils/detectBrowserInfo';

const useAboutDataSource = () => {
    const { t } = useTranslation();
    const accountId = useSelector(selectAccountId);
    const deviceId = useSelector(selectClientId);
    const epgLastUpdated = useSelector(getEpgLastUpdated);

    const [hardwareDeviceVersion, setHardwareDeviceVersion] = useState('');

    const epgLastUpdatedFormatted = useMemo(
        () =>
            epgLastUpdated && format(epgLastUpdated, 'yyyy.MM.dd / HH:mm:ss O'),
        [epgLastUpdated],
    );

    const getCapitalizedString = useCallback((text: string) => {
        return `${text[0].toUpperCase()}${text.slice(1).toLowerCase()}`;
    }, []);

    const getHardwareBrand = useCallback(() => {
        const brand = getBrand();

        return getCapitalizedString(brand);
    }, [getCapitalizedString]);

    const environment = useMemo(
        () => getCapitalizedString(ENV.ENV_NAME || ''),
        [getCapitalizedString],
    );

    const hardwareData = useMemo(
        () =>
            Platform.select({
                web: [
                    {
                        id: SYSTEM_INFORMATION_SOFTWARE_VERSION,
                        title: SYSTEM_INFORMATION_SOFTWARE_VERSION,
                        rightTxt: `${detectBrowserInfo().browser} ${
                            detectBrowserInfo().version
                        }`,
                    },
                ],
                default: [
                    {
                        id: SYSTEM_INFORMATION_HARDWARE_BRAND_NAME,
                        title: SYSTEM_INFORMATION_HARDWARE_BRAND_NAME,
                        rightTxt:
                            getHardwareBrand() ||
                            t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
                    },
                    {
                        id: SYSTEM_INFORMATION_MODEL_NAME,
                        title: SYSTEM_INFORMATION_MODEL_NAME,
                        rightTxt:
                            getModel() ||
                            t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
                    },
                    ...(Platform.OS === 'android'
                        ? [
                              {
                                  id: SYSTEM_INFORMATION_HARDWARE_VERSION,
                                  title: SYSTEM_INFORMATION_HARDWARE_VERSION,
                                  rightTxt:
                                      hardwareDeviceVersion ||
                                      t(
                                          SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE,
                                      ),
                              },
                          ]
                        : []),
                    {
                        id: SYSTEM_INFORMATION_SOFTWARE_VERSION,
                        title: SYSTEM_INFORMATION_SOFTWARE_VERSION,
                        rightTxt:
                            getSystemVersion() ||
                            t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
                    },
                ],
            }),
        [t, getHardwareBrand, hardwareDeviceVersion],
    );

    useEffect(() => {
        getDevice().then(setHardwareDeviceVersion);
    }, []);

    return useMemo<SimpleSettingsItem[]>(
        () => [
            ...hardwareData,
            {
                id: SYSTEM_INFORMATION_EPG_DATA,
                title: SYSTEM_INFORMATION_EPG_DATA,
                rightTxt:
                    epgLastUpdatedFormatted ||
                    t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
            },
            {
                id: SYSTEM_INFORMATION_ACCOUNT_ID,
                title: SYSTEM_INFORMATION_ACCOUNT_ID,
                rightTxt:
                    accountId || t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
            },
            {
                id: SYSTEM_INFORMATION_UI_VERSION,
                title: SYSTEM_INFORMATION_UI_VERSION,
                rightTxt:
                    `${Platform.select({
                        web: version,
                        default: getVersion(),
                    })} (${buildNumber})` ||
                    t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
            },
            {
                id: SYSTEM_INFORMATION_ENVIRONMENT,
                title: SYSTEM_INFORMATION_ENVIRONMENT,
                rightTxt:
                    environment || t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
            },
            {
                id: SYSTEM_INFORMATION_UUID,
                title: SYSTEM_INFORMATION_UUID,
                rightTxt:
                    deviceId || t(SCREEN_SETTINGS_ABOUT_INFO_NOT_AVAILABLE),
            },
        ],
        [
            t,
            accountId,
            deviceId,
            environment,
            epgLastUpdatedFormatted,
            hardwareData,
        ],
    );
};

export default useAboutDataSource;
